import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Briefcase } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    document.documentElement.classList.add("theme-light");
    return () => document.documentElement.classList.remove("theme-light");
  }, []);

  useEffect(() => {
    if (user && user.role) {
      if (user.role === "admin" || user.role === "staff") navigate("/admin");
      else if (user.role === "candidate") navigate("/candidate/dashboard");
      else if (user.role === "employer") navigate("/employer/dashboard");
    }
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(form.email, form.password);
      toast.success("Welcome back!");
      if (data.role === "admin" || data.role === "staff") navigate("/admin");
      else if (data.role === "candidate") navigate("/candidate/dashboard");
      else if (data.role === "employer") navigate("/employer/dashboard");
      else navigate("/");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#F8FAFC] section-radial px-4">
      <Seo title="Login" noindex />
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center mb-6">
          <img src="/logo.png" alt="Dreamzz HR & Consultancy" className="h-16 w-auto" />
        </Link>
        <form onSubmit={submit} className="glass-strong rounded-2xl p-8 grid gap-4">
          <h1 className="font-display text-2xl font-bold text-center">Sign In</h1>
          <div><Label>Email</Label><Input data-testid="login-email" type="email" required value={form.email} onChange={upd("email")} className="bg-white/5 border-white/10 mt-1" /></div>
          <div><Label>Password</Label><Input data-testid="login-password" type="password" required value={form.password} onChange={upd("password")} className="bg-white/5 border-white/10 mt-1" /></div>
          <Button data-testid="login-submit" type="submit" disabled={loading} className="btn-press bg-brand-emerald hover:bg-[#C62828] text-white font-semibold h-12">
            {loading ? "Signing in..." : "Sign In"}
          </Button>
          <p className="text-center text-sm text-slate-400">New candidate? <Link to="/candidate-registration" className="text-brand-emerald">Register</Link></p>
        </form>
      </div>
    </div>
  );
}
